import type { GetStaticPropsContext } from 'next';

import { INDEX_MAP } from '#/lib/algolia';
import { fetchBlogCategoryPaths } from '#/lib/sanity/fetchers/fetchBlogCategoryPaths';
import { fetchBlogCategoryProps } from '#/lib/sanity/fetchers/fetchBlogCategoryProps';
import type { BlogProps } from '#/lib/sanity/fetchers/fetchBlogProps';
import { BlogPostHit } from '#/components/BlogPostHit';
import { Headline } from '#/components/Headline';
import { ListingFilters } from '#/components/ListingFilters';
import { SearchableListing } from '#/components/SearchableListing';

export const config = {
  maxDuration: 30,
};

export async function getStaticPaths() {
  const paths = await fetchBlogCategoryPaths();

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string }>) {
  const props = await fetchBlogCategoryProps(params!.slug, draftMode);

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props };
}

export default function BlogCategory(props: BlogProps) {
  const data = props.page;

  // Add Case Studies to blog filter sidebar
  const filters = [
    ...data.filters,
    {
      title: 'Case Studies',
      href: '/case-studies',
      _key: 'caseStudies',
      slug: '/case-studies',
      isActive: false,
    },
  ];

  return (
    <SearchableListing
      filters={`category:${data.slug} AND isHiddenFromListing:false`}
      indexName={INDEX_MAP.POSTS}
      headline={data.pageTitle}
      description={data.description}
      searchInputPlaceholder="Search"
      classNames={{
        list: 'grid grid-cols-12 gap-x-20 gap-y-40',
        item: 'col-span-full m:col-span-6',
      }}
      preListingComponent={() => (
        <Headline
          value={data.title}
          as="h3"
          variant={3}
          className="mb-20 m:mb-50"
        />
      )}
      sidebarComponent={() => <ListingFilters filters={filters} />}
      hitComponent={({ hit }) => (
        <BlogPostHit
          hit={hit}
          mediaSizes={['100vw', { '600px': '50vw', l: '33vw' }]}
        />
      )}
    />
  );
}
